'use client';

import React from 'react';
import { Box, Text, HStack } from '@chakra-ui/react';
import { LuChevronRight, LuLayoutDashboard } from 'react-icons/lu';

import { NavigationItem, getBreadcrumbItems } from '../navigation-config';

export interface BreadcrumbsProps {
  pathname?: string;
  items?: NavigationItem[];
  homeLabel?: string;
  homeHref?: string;
  showHome?: boolean;
  separator?: React.ReactNode;
  maxItems?: number;
  onNavigate?: (href: string) => void;
  className?: string;
}

export function Breadcrumbs({
  pathname,
  items,
  homeLabel = 'Home',
  homeHref = '/',
  showHome = true,
  separator,
  maxItems,
  onNavigate,
  className,
}: BreadcrumbsProps) {
  const breadcrumbItems = React.useMemo(() => {
    if (items) return items;
    if (pathname) return getBreadcrumbItems(pathname);
    return [];
  }, [items, pathname]);

  const displayItems = React.useMemo(() => {
    const allItems = showHome
      ? [
          {
            id: 'home',
            label: homeLabel,
            href: homeHref,
            icon: LuLayoutDashboard,
          },
          ...breadcrumbItems,
        ]
      : breadcrumbItems;

    if (!maxItems || allItems.length <= maxItems) {
      return allItems;
    }

    // If we have more items than maxItems, show ellipsis
    const firstItem = allItems[0];
    const lastItems = allItems.slice(-(maxItems - 2));
    return [firstItem, { id: 'ellipsis', label: '...', href: undefined }, ...lastItems];
  }, [breadcrumbItems, showHome, homeLabel, homeHref, maxItems]);

  const handleClick = (href: string, e: React.MouseEvent) => {
    if (onNavigate) {
      e.preventDefault();
      onNavigate(href);
    }
  };

  return (
    <HStack gap={2} align="center" className={className}>
      {displayItems.map((item, index) => {
        const isLast = index === displayItems.length - 1;
        const Icon = item.icon;

        return (
          <React.Fragment key={item.id}>
            {item.id === 'ellipsis' ? (
              <Text color="#6B7280" fontSize="sm">
                ...
              </Text>
            ) : isLast ? (
              <HStack gap={1} align="center">
                {Icon && <Icon size={16} />}
                <Text color="#111827" fontWeight="medium" fontSize="sm">
                  {item.label}
                </Text>
              </HStack>
            ) : (
              <a
                href={item.href}
                onClick={(e) => item.href && handleClick(item.href, e)}
                style={{
                  display: 'inline-flex',
                  alignItems: 'center',
                  gap: '0.25rem',
                  color: '#6B7280',
                  fontSize: '0.875rem',
                  textDecoration: 'none',
                  cursor: 'pointer',
                }}
                onMouseEnter={(e) => {
                  e.currentTarget.style.color = '#111827';
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.color = '#6B7280';
                }}
              >
                {Icon && <Icon size={16} />}
                {item.label}
              </a>
            )}
            {!isLast && <Box color="#9CA3AF">{separator || <LuChevronRight size={16} />}</Box>}
          </React.Fragment>
        );
      })}
    </HStack>
  );
}

// Simplified breadcrumb for basic use cases
export interface SimpleBreadcrumbsProps {
  items: Array<{ label: string; href?: string }>;
  separator?: React.ReactNode;
  onNavigate?: (href: string) => void;
  className?: string;
}

export function SimpleBreadcrumbs({
  items,
  separator,
  onNavigate,
  className,
}: SimpleBreadcrumbsProps) {
  const handleClick = (href: string, e: React.MouseEvent) => {
    if (onNavigate) {
      e.preventDefault();
      onNavigate(href);
    }
  };

  return (
    <HStack gap={2} align="center" className={className}>
      {items.map((item, index) => {
        const isLast = index === items.length - 1;

        return (
          <React.Fragment key={index}>
            {isLast ? (
              <Text color="#111827" fontWeight="medium" fontSize="sm">
                {item.label}
              </Text>
            ) : (
              <a
                href={item.href}
                onClick={(e) => item.href && handleClick(item.href, e)}
                style={{
                  color: '#6B7280',
                  fontSize: '0.875rem',
                  textDecoration: 'none',
                  cursor: 'pointer',
                }}
                onMouseEnter={(e) => {
                  e.currentTarget.style.color = '#111827';
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.color = '#6B7280';
                }}
              >
                {item.label}
              </a>
            )}
            {!isLast && <Box color="#9CA3AF">{separator || <LuChevronRight size={16} />}</Box>}
          </React.Fragment>
        );
      })}
    </HStack>
  );
}

// Breadcrumb compound components for compatibility
export const Breadcrumb = {
  Root: ({ children, className }: { children: React.ReactNode; className?: string }) => (
    <nav aria-label="Breadcrumb" className={className}>
      {children}
    </nav>
  ),
  List: ({ children }: { children: React.ReactNode }) => (
    <HStack gap={2} align="center">
      {children}
    </HStack>
  ),
  Item: ({ children }: { children: React.ReactNode }) => (
    <Box as="li" display="inline-flex" alignItems="center">
      {children}
    </Box>
  ),
  Link: ({
    children,
    href,
    onClick,
    ...props
  }: {
    children: React.ReactNode;
    href?: string;
    onClick?: (e: React.MouseEvent) => void;
    [key: string]: unknown;
  }) => (
    <a
      href={href}
      onClick={onClick}
      style={{
        color: '#6B7280',
        fontSize: '0.875rem',
        textDecoration: 'none',
        cursor: 'pointer',
      }}
      onMouseEnter={(e) => {
        e.currentTarget.style.color = '#111827';
      }}
      onMouseLeave={(e) => {
        e.currentTarget.style.color = '#6B7280';
      }}
      {...props}
    >
      {children}
    </a>
  ),
  Separator: ({ children }: { children?: React.ReactNode }) => (
    <Box as="span" color="#9CA3AF" marginX={2}>
      {children || <LuChevronRight size={16} />}
    </Box>
  ),
  Ellipsis: () => (
    <Text color="#6B7280" fontSize="sm">
      ...
    </Text>
  ),
};

// Export individual components for compatibility
export const BreadcrumbItem = Breadcrumb.Item;
export const BreadcrumbLink = Breadcrumb.Link;
export const BreadcrumbSeparator = Breadcrumb.Separator;

// Export legacy names for backwards compatibility
export { Breadcrumb as BreadcrumbRoot };
export { Breadcrumb as BreadcrumbComponent };
